import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { readAuthorSetupIdentity, setupRequest, normalizeSetupStripe } from '../../auth/authorSetup';

const STATUS_API = 'https://fpb36g137b.execute-api.us-west-2.amazonaws.com/default/checkStripeStatus';

export default function StripeOnboarding() {
  const [checking, setChecking] = useState(true);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const request = useRef(null);
  const locked = useRef(true);
  const heading = useRef(null);

  useEffect(() => {
    let active = true;
    // Reuse the initial check during StrictMode effect replay.
    if (!request.current) {
      request.current = (async () => {
        const identity = await readAuthorSetupIdentity();
        const data = await setupRequest(STATUS_API, identity, { userSub: identity.userSub });
        return normalizeSetupStripe(data);
      })();
    }
    request.current.then(result => {
      if (active) setStatus(result);
    }).catch(() => {
      if (active) setError('We couldn’t confirm your payment status. Refresh to try again. If your session has ended, return to Author Login.');
    }).finally(() => {
      if (active) { locked.current = false; setChecking(false); }
    });
    return () => { active = false; };
  }, [attempt]);

  useEffect(() => { if (!checking) heading.current?.focus(); }, [checking]);
  function refresh() {
    if (locked.current) return;
    locked.current = true; request.current = null;
    setChecking(true); setStatus(null); setError('');
    setAttempt(value => value + 1);
  }
  const title = checking ? 'Checking your payment connection' : error ? 'Payment status is unavailable' :
    status?.ready ? 'Your payments are ready' : status?.accountId || status?.pending ? 'Your payment setup needs attention' : 'Connect payments for your stories';
  return <main className="min-h-screen bg-bookish-cream text-bookish-royal px-4 sm:px-6 py-8">
    <div className="max-w-2xl mx-auto bg-white rounded-2xl border border-bookish-purple/20 p-6 sm:p-8 shadow-sm">
      <p className="text-sm uppercase tracking-widest mb-3">Your Author payment connection</p>
      <h1 ref={heading} tabIndex={-1} className="text-3xl font-serif font-bold mb-4">{title}</h1>
      <p className="mb-5">Make the next chapter ready for your readers. This page checks your Stripe connection for book-sale payments and payouts. Your Author membership is managed separately in setup.</p>
      <div aria-busy={checking}>
        <p role="status" className="my-4">{checking ? 'Checking the latest payment status for your Author account…' :
          error ? '' : status?.ready ? 'Stripe reports that charges and payouts are enabled, your details are submitted, and no current requirements are due.' :
          status?.pending ? 'An earlier Stripe setup attempt is unfinished. Continue payment setup to safely resume it.' :
          status?.unknown ? 'Some payment details could not be confirmed. Continue payment setup to review your connection, or refresh this status.' :
          status?.accountId ? 'Your Stripe account is connected, but it isn’t ready for book-sale payments yet. Continue setup to review the remaining steps.' :
          'No Stripe payment account is connected yet. Continue setup when you’re ready to receive payments for your books.'}</p>
        <p role="alert" className="my-4">{error}</p>
        {!checking && !error && status?.due && <p className="mb-4">Stripe needs more information. Continue payment setup to review its requirements.</p>}
      </div>
      <p className="text-sm mb-5">Returning from Stripe does not always mean setup is finished. After completing a step there, refresh to check the latest status. You can add and edit book drafts while you finish connecting payments.</p>
      <div className="flex flex-wrap gap-4">
        <button type="button" disabled={checking} onClick={refresh} className="min-h-[44px] bg-bookish-royal hover:bg-bookish-purpleAccent text-white rounded-lg px-5 py-3 disabled:opacity-50">{checking ? 'Checking status…' : 'Refresh status'}</button>
        <Link className="underline py-3" to="/authorportal/letsgetstarted?step=4">Continue payment setup</Link>
        <Link className="underline py-3" to="/authorportal/mainpage">Author dashboard</Link>
      </div>
      {error && <Link className="inline-block underline py-3" to="/loginauthor">Return to Author Login</Link>}
      <footer className="mt-6 flex flex-wrap gap-4">
        <Link className="underline py-3" to="/authorportal/letsgetstarted?step=1">Review membership setup</Link>
        <a className="underline py-3" href="mailto:author@hayleyraejohnson.com">Need a hand? Contact Lady Liza</a>
      </footer>
    </div>
  </main>;
}
